<template>
  <div class="md-layout">
    <CSLoading :is-loading="isLoading"></CSLoading>
    <div class="md-layout-item md-size-100">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <md-icon>signpost</md-icon>
          </div>
          <h4 class="title">{{ $t("streetDirectory.title") }}</h4>
        </md-card-header>
        <md-card-content>
          <div class="md-layout">
            <div class="md-layout-item md-size-10">
              <md-field md-clearable>
                <label>{{ $t("streetDirectory.zipcode") }}</label>
                <md-input v-model="filter.zipcode" @input="debounceLoad"></md-input>
              </md-field>
            </div>
            <div class="md-layout-item md-size-20">
              <md-field md-clearable>
                <label>{{ $t("streetDirectory.town") }}</label>
                <md-input v-model="filter.town" @input="debounceLoad"></md-input>
              </md-field>
            </div>
            <div class="md-layout-item md-size-25">
              <md-field md-clearable>
                <label>{{ $t("streetDirectory.street") }}</label>
                <md-input v-model="filter.street" @input="debounceLoad"></md-input>
              </md-field>
            </div>
            <div class="md-layout-item md-size-10">
              <md-field md-clearable>
                <label>{{ $t("streetDirectory.source") }}</label>
                <md-select v-model="filter.source" @md-selected="debounceLoad">
                  <md-option v-for="src in sourceOptions" :key="src" :value="src">{{ flags[src] }} {{ src }}</md-option>
                </md-select>
              </md-field>
            </div>
            <div class="md-layout-item md-size-35 text-right" style="display: flex; align-items: center; justify-content: flex-end; gap: 12px">
              <span>{{ $t("streetDirectory.entries") }}: {{ totalItems.toLocaleString() }}</span>
              <md-button class="md-info md-icon-button" :title="$t('streetDirectory.overview.title')" @click="openOverview">
                <md-icon>info</md-icon>
              </md-button>
            </div>
          </div>

          <md-table>
            <md-table-row>
              <md-table-head>{{ $t("streetDirectory.zipcode") }}</md-table-head>
              <md-table-head>{{ $t("streetDirectory.town") }}</md-table-head>
              <md-table-head>{{ $t("streetDirectory.street") }}</md-table-head>
              <md-table-head>{{ $t("streetDirectory.source") }}</md-table-head>
              <md-table-head>{{ $t("streetDirectory.importedOn") }}</md-table-head>
            </md-table-row>
            <md-table-row v-for="entry in entries" :key="entry.id">
              <md-table-cell>{{ entry.zipcode }}</md-table-cell>
              <md-table-cell>{{ entry.town }}</md-table-cell>
              <md-table-cell>{{ entry.street }}</md-table-cell>
              <md-table-cell>{{ entry.source }}</md-table-cell>
              <md-table-cell>{{ entry.importedOn | formatDate }}</md-table-cell>
            </md-table-row>
          </md-table>

          <div class="text-right" style="display: flex; align-items: center; justify-content: flex-end; gap: 8px; padding: 8px 0">
            <md-button class="md-icon-button" :disabled="pageIndex === 0" @click="changePage(-1)"><md-icon>chevron_left</md-icon></md-button>
            <span>{{ pageIndex + 1 }} / {{ Math.max(1, Math.ceil(totalItems / pageSize)) }}</span>
            <md-button class="md-icon-button" :disabled="(pageIndex + 1) * pageSize >= totalItems" @click="changePage(1)"><md-icon>chevron_right</md-icon></md-button>
          </div>
        </md-card-content>
      </md-card>
    </div>

    <modal v-if="overviewModal" @close="overviewModal = false" class-name="street-overview-modal">
      <template slot="header">
        <h4 class="modal-title">{{ $t("streetDirectory.overview.title") }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="overviewModal = false">
          <md-icon>clear</md-icon>
        </md-button>
      </template>
      <template slot="body">
        <table class="overview-table">
          <thead>
            <tr>
              <th>{{ $t("streetDirectory.overview.country") }}</th>
              <th>{{ $t("streetDirectory.source") }}</th>
              <th class="text-right">{{ $t("streetDirectory.entries") }}</th>
              <th>{{ $t("streetDirectory.overview.note") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in overviewRows" :key="row.source">
              <td><span class="flag">{{ flags[row.source] }}</span> {{ row.country }}</td>
              <td>{{ row.source }}</td>
              <td class="text-right">{{ row.count === null ? "…" : row.count.toLocaleString() }}</td>
              <td>{{ row.note }}</td>
            </tr>
          </tbody>
        </table>
      </template>
      <template slot="footer">
        <md-button class="md-secondary" @click="overviewModal = false">{{ $t("orders.close") }}</md-button>
      </template>
    </modal>
  </div>
</template>
<script>
import { debounce } from "lodash";
import axiosFactory from "../../services/axios.factory";
import CSLoading from "../../components/Loading/CSLoading.vue";
import { Modal } from "@/components";

export default {
  components: {
    CSLoading,
    Modal,
  },
  data() {
    return {
      isLoading: false,
      entries: [],
      totalItems: 0,
      pageIndex: 0,
      pageSize: 50,
      sourceOptions: ["osm", "leitdaten", "at", "fr", "be", "nl", "cz", "it", "es"],
      overviewModal: false,
      overviewRows: [],
      flags: { osm: "🇩🇪", leitdaten: "🇩🇪", at: "🇦🇹", fr: "🇫🇷", be: "🇧🇪", nl: "🇳🇱", cz: "🇨🇿", it: "🇮🇹", es: "🇪🇸" },
      filter: {
        zipcode: "",
        town: "",
        street: "",
        source: null,
      },
    };
  },
  created() {
    this.debounceLoad = debounce(() => {
      this.pageIndex = 0;
      this.loadEntries();
    }, 400);
  },
  mounted() {
    this.loadEntries();
  },
  methods: {
    loadEntries() {
      this.isLoading = true;
      axiosFactory()
        .get("/street_directory", {
          params: {
            zipcode: this.filter.zipcode || undefined,
            town: this.filter.town || undefined,
            street: this.filter.street || undefined,
            source: this.filter.source || undefined,
            pageIndex: this.pageIndex,
            pageSize: this.pageSize,
          },
        })
        .then((res) => {
          this.entries = res.data.data;
          this.totalItems = res.data.total;
        })
        .catch((e) => {
          console.log(e);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    openOverview() {
      this.overviewRows = ["osm", "at", "fr", "be", "nl", "cz", "it", "es", "leitdaten"].map((source) => ({
        source,
        country: this.$t(`streetDirectory.overview.countries.${source}`),
        note: this.$t(`streetDirectory.overview.notes.${source}`),
        count: null,
      }));
      this.overviewModal = true;
      this.overviewRows.forEach((row) => {
        axiosFactory()
          .get("/street_directory", { params: { source: row.source, pageIndex: 0, pageSize: 1 } })
          .then((res) => {
            row.count = res.data.total;
          })
          .catch(() => {
            row.count = 0;
          });
      });
    },
    changePage(delta) {
      this.pageIndex = Math.max(0, this.pageIndex + delta);
      this.loadEntries();
    },
  },
};
</script>
<style>
.modal-container.street-overview-modal {
  width: 900px;
  max-width: min(900px, 95vw) !important;
}
</style>
<style scoped>
.flag {
  font-size: 16px;
  margin-right: 4px;
}
.overview-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 13px;
}
.overview-table th,
.overview-table td {
  padding: 5px 10px;
  border-bottom: 1px solid #eee;
  vertical-align: top;
}
/* Land/Quelle/Eintraege nicht umbrechen — nur der Hinweis darf mehrzeilig sein */
.overview-table td:nth-child(1),
.overview-table td:nth-child(2),
.overview-table td:nth-child(3),
.overview-table th:nth-child(1),
.overview-table th:nth-child(2),
.overview-table th:nth-child(3) {
  white-space: nowrap;
}
.overview-table .text-right {
  text-align: right;
}
</style>
